import React, { useEffect, useState } from 'react';
import { GlobalStyles } from '@mui/material';
import { profileApi } from '../../services/profileApi';
import http from '../../services/http';
import { internApi } from '../../services/internApi';
import ProfileCard from '../../components/profile/ProfileCard';
import ProfileForm from '../../components/profile/ProfileForm';
import { useParams } from 'react-router-dom';


function ymd(d) {
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const da = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  } catch {
    return '';
  }
}
function prettyDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d || '');
  }
}


// Global dark-friendly styles just for this page
function DarkFormStyles() {
  return (
    <GlobalStyles styles={(theme) => ({
      '.MuiInputBase-root': {
        backgroundColor: theme.palette.mode === 'dark'
          ? theme.palette.background.paper
          : theme.palette.common.white,
        color: theme.palette.text.primary,
      },
      '.MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
      '.MuiInputBase-input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
      'input, textarea, select': {
        backgroundColor: theme.palette.mode === 'dark'
          ? theme.palette.background.paper
          : theme.palette.common.white,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      'input::placeholder, textarea::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
    })}/>
  );
}

export default function ProfileEditor({ userId: propUserId }) {
  const params = useParams();
  const userId = propUserId || params?.userId;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [kpiBusy, setKpiBusy] = useState(false);
  const [kpi, setKpi] = useState({ visits: 0, pending: 0, activities: 0, reviewerQueue: 0 });
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [actions, setActions] = useState([]);

  async function load() {
    if (!userId) { setError('No userId provided'); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await profileApi.getByUserId(userId);
      setProfile(res?.profile || res || {});
    } catch (e) {
      setError(e.message || 'Failed to load profile');
      if (e?.response?.status === 401) setTimeout(() => { window.location.href = '/cmfm-login'; }, 1200);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  async function handleSave(form) {
    setSaving(true);
    setError('');
    try {
      const res = await profileApi.updateByUserId(userId, form);
      setProfile(res?.profile || res || {});
    } catch (e) {
      setError(e.message || 'Failed to save profile');
      if (e?.response?.status === 401) setTimeout(() => { window.location.href = '/cmfm-login'; }, 1200);
    } finally {
      setSaving(false);
    }
  }

  async function loadKPIs() {
    if (kpiBusy) return;
    try {
      setKpiBusy(true);
      const now = new Date();
      const from = ymd(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
      const to = ymd(now);

      const vCountRes = await http.get(`/api/visits?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&count=1`);
      const visits = vCountRes?.data?.count ?? vCountRes?.data?.total ?? 0;

      const pendingRes = await http.get(`/api/visits/pending?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=200`);
      const pending = Array.isArray(pendingRes?.data?.visits) ? pendingRes.data.visits.length : 0;

      const actRes = await internApi.listActivitiesMine({ limit: 200 });
      const activitiesArr =
        actRes?.activities ||
        actRes?.items ||
        actRes?.data?.activities ||
        actRes?.data?.items ||
        [];
      const activities = Array.isArray(activitiesArr) ? activitiesArr.length : 0;

      let reviewerQueue = 0;
      try {
        const rqRes = await internApi.reviewerQueue({ limit: 200 });
        const rqArr =
          rqRes?.items ||
          rqRes?.queue ||
          rqRes?.data?.items ||
          rqRes?.data?.queue ||
          [];
        reviewerQueue = Array.isArray(rqArr) ? rqArr.length : 0;
      } catch {}

      setKpi({ visits, pending, activities, reviewerQueue });

      const next = [];
      if (pending > 0) next.push({ label: 'Pending prescription approvals', value: pending, hint: 'Visit Approvals' });
      if (reviewerQueue > 0) next.push({ label: 'Intern activities to review', value: reviewerQueue, hint: 'Reviewer Queue' });
      setActions(next);
    } catch (e) {
      console.warn('Profile KPIs load failed', e?.message || e);
    } finally {
      setKpiBusy(false);
    }
  }

  async function loadTimeline() {
    if (timelineBusy) return;
    try {
      setTimelineBusy(true);
      const now = new Date();
      const from = ymd(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
      const to = ymd(now);

      const vRes = await http.get(`/api/visits?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=20`);
      const visitsArr = Array.isArray(vRes?.data?.visits) ? vRes.data.visits : [];

      const actRes = await internApi.listActivitiesMine({ limit: 20 });
      const activitiesArr =
        actRes?.activities ||
        actRes?.items ||
        actRes?.data?.activities ||
        actRes?.data?.items ||
        [];
      const acts = Array.isArray(activitiesArr) ? activitiesArr : [];

      const items = [];

      for (const v of visitsArr) {
        items.push({
          type: 'visit',
          ts: v?.visitDate || v?.createdAt || v?.updatedAt,
          title: `OPD Visit • ${v?.centre || v?.chc || 'Centre'}`,
          subtitle: v?.citizenId ? `Citizen: ${v.citizenId}` : (v?.visitId ? `Visit: ${v.visitId}` : ''),
          status: v?.prescription?.status || v?.status || '',
        });
      }

      for (const a of acts) {
        items.push({
          type: 'activity',
          ts: a?.updatedAt || a?.createdAt,
          title: `Intern Activity • ${a?.activityName || a?.title || a?.type || 'Activity'}`,
          subtitle: a?.centre ? `Centre: ${a.centre}` : (a?.posting ? `Posting: ${a.posting}` : ''),
          status: a?.status || '',
        });
      }

      items.sort((x, y) => (new Date(y.ts || 0).getTime() - new Date(x.ts || 0).getTime()));
      setTimeline(items.slice(0, 20));
    } catch (e) {
      console.warn('Profile timeline load failed', e?.message || e);
    } finally {
      setTimelineBusy(false);
    }
  }

  useEffect(() => {
    if (!loading && !error) {
      loadKPIs();
      loadTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error]);



  return (
    <div className="p-4 space-y-6 cmfm-profile">
      <DarkFormStyles />

      <div>
        <h1 className="text-2xl font-semibold">Edit User Profile / उपयोगकर्ता प्रोफ़ाइल संपादित करें</h1>
        <p className="text-sm opacity-70">Faculty/Admin can update staff profiles. / फैकल्टी/एडमिन प्रोफ़ाइल अपडेट कर सकते हैं।</p>
      </div>

      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : error ? (
        <div className="text-red-600 dark:text-red-400">{error}</div>
      ) : (
        <>
          
          {/* Work dashboard (Stage 2) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Your work (last 30 days)</div>
                  <div className="text-xs opacity-70 mt-0.5">Auto-generated from Visits + Intern Activities.</div>
                </div>
                <button
                  type="button"
                  onClick={() => { loadKPIs(); loadTimeline(); }}
                  disabled={kpiBusy || timelineBusy}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border ${
                    (kpiBusy || timelineBusy)
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 text-purple-900 border-purple-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-100 dark:border-neutral-700'
                  }`}
                >
                  {(kpiBusy || timelineBusy) ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">My Visits</div>
                  <div className="text-xl font-semibold mt-1">{kpi.visits}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">Pending Approvals</div>
                  <div className="text-xl font-semibold mt-1">{kpi.pending}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">Activities</div>
                  <div className="text-xl font-semibold mt-1">{kpi.activities}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">To Review</div>
                  <div className="text-xl font-semibold mt-1">{kpi.reviewerQueue}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">Recent timeline</div>
                {timelineBusy ? (
                  <div className="text-sm opacity-70">Loading timeline…</div>
                ) : timeline.length ? (
                  <div className="space-y-2">
                    {timeline.map((it, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3">
                        <div>
                          <div className="text-sm font-semibold">{it.title}</div>
                          {it.subtitle ? <div className="text-xs opacity-70 mt-0.5">{it.subtitle}</div> : null}
                          {it.status ? <div className="text-xs opacity-70 mt-0.5">Status: {it.status}</div> : null}
                        </div>
                        <div className="text-xs opacity-60 whitespace-nowrap">{prettyDate(it.ts)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm opacity-70">No recent activity found.</div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
              <div className="text-sm font-semibold">Next actions</div>
              <div className="text-xs opacity-70 mt-0.5">Auto-suggested based on pending work.</div>

              <div className="mt-3 space-y-2">
                {actions?.length ? (
                  actions.map((a, idx) => (
                    <div key={idx} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800 p-3">
                      <div className="text-sm font-semibold">{a.label}</div>
                      <div className="text-xs opacity-70 mt-0.5">
                        {String(a.value)}{a.hint ? ` • ${a.hint}` : ''}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm opacity-70">No pending actions detected.</div>
                )}
              </div>
            </div>
          </div>

<ProfileCard profile={profile} />
          <div className="rounded-xl shadow-md p-4 bg-white dark:bg-neutral-900 dark:text-neutral-100">
            <ProfileForm initial={profile} onSave={handleSave} saving={saving} />
          </div>
        </>
      )}
    </div>
  );
}
